import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEqual } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';

import { IGlobalSettingNodeDto } from '@assess/browser/chimera-api';

import { GlobalSettingTree } from './GlobalSettingTree';

@UntilDestroy()
@Component({
  selector: 'assess-provider-filter',
  templateUrl: './provider-filter.component.html',
  styleUrls: ['./provider-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() providers: IGlobalSettingNodeDto[] = [];
  @Input() value: string[] | undefined = [];
  @Input() clear = new BehaviorSubject<boolean>(false);

  @Output()
  readonly selected = new EventEmitter<string[]>();

  readonly filterControl = new FormControl();
  tree!: GlobalSettingTree;

  constructor(private readonly ref: ChangeDetectorRef) {}

  ngOnInit() {
    this.filterControl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(250),
        distinctUntilChanged(),
        tap((filterText) => {
          this.tree.filter(filterText);
          this.ref.detectChanges();
        })
      )
      .subscribe();

    this.clear
      .pipe(
        untilDestroyed(this),
        filter((result) => result),
        tap(() => {
          this.filterControl.setValue('');
          this.tree.deselectAll();
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // check reference equality only - it'll prob be faster to recreate than do deep equality
    if (changes.providers?.currentValue !== changes.providers?.previousValue) {
      // clean up any possible subscriptions
      this.tree?.dispose();

      this.tree = new GlobalSettingTree(changes.providers.currentValue, true);

      this.tree.selections
        .pipe(
          untilDestroyed(this),
          tap((data) => this.selected.emit(data))
        )
        .subscribe();
    }

    // check deep equality so tree doesn't re-trigger selections
    if (changes.value?.currentValue && !isEqual(changes.value?.currentValue, changes.value?.previousValue)) {
      this.tree.setSelectedIds(changes.value.currentValue);
    }
  }

  ngOnDestroy() {
    this.tree?.dispose();
  }
}
